import React, { useState } from "react";

const MILESTONES = [
  { number: 0,    label: "Getting\nStarted",  emoji: null },
  { number: 3,    label: "Beta\nAccess",       emoji: null },
  { number: 5,    label: "Premium\nTrial",     emoji: null },
  { number: "🏆", label: "Founding\nMember",   emoji: true },
];

const ShareCardInline = ({
  referralLink = "",
  referralCount = 0,
  onClose,
  deviceType = "mobile",
}) => {
  const [copied, setCopied] = useState(false);
  const isTablet = deviceType === "tablet";

  const handleCopy = async () => {
    if (referralLink && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(referralLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        // silently fail
      }
    }
  };

  const badgeSize = isTablet ? 58 : 48;
  const badgeFontSize = isTablet ? "32px" : "26px";
  const countFontSize = isTablet ? "120px" : "90px";

  return (
    <>
      <style>{`
        @keyframes shareCardSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(6px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: "1.5rem",
        }}
        onClick={onClose}
      >
      <div
        style={{
          width: "100%",
          maxWidth: isTablet ? "520px" : "400px",
          borderRadius: "24px",
          overflow: "hidden",
          backgroundImage: `url('/popup/popup-bg.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "#1a3a5c",
          animation: "shareCardSlideUp 300ms ease-out both",
          position: "relative",
          boxShadow: "0 20px 40px rgba(0,0,0,0.35)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close referral card"
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.9)",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "20px",
            color: "#0D0E20",
            lineHeight: 1,
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          ×
        </button>

        {/* Inner content */}
        <div
          style={{
            padding: isTablet ? "32px 28px 28px" : "28px 20px 24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: isTablet ? "16px" : "14px",
          }}
        >
          {/* Referral count */}
          <div
            style={{
              fontSize: countFontSize,
              fontFamily: "Inter, sans-serif",
              fontWeight: "400",
              color: "#3B6EC1",
              lineHeight: "1",
              textAlign: "center",
              textShadow: "0 2px 12px rgba(0,0,0,0.2)",
            }}
          >
            {referralCount}
          </div>

          {/* Message */}
          <div
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: isTablet ? "18px" : "15px",
              fontWeight: "600",
              color: "#FFFFFF",
              textAlign: "center",
              lineHeight: "1.4",
              textShadow: "0 2px 8px rgba(0,0,0,0.5)",
            }}
          >
            Winners thrive in teams.
            <br />
            Share your link with your teammates.
          </div>

          {/* Copy link bar */}
          <div
            style={{
              width: "100%",
              backgroundColor: "rgba(217, 217, 217, 0.7)",
              borderRadius: "22px",
              padding: "10px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                flex: 1,
                height: isTablet ? "52px" : "44px",
                backgroundColor: "rgba(255,255,255,0.95)",
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                padding: "0 12px",
                overflow: "hidden",
              }}
            >
              <input
                type="text"
                readOnly
                value={referralLink}
                style={{
                  width: "100%",
                  border: "none",
                  outline: "none",
                  backgroundColor: "transparent",
                  fontSize: isTablet ? "13px" : "12px",
                  fontFamily: "ui-monospace, monospace",
                  color: "#374151",
                }}
              />
            </div>

            <button
              onClick={handleCopy}
              style={{
                height: isTablet ? "52px" : "44px",
                padding: isTablet ? "0 22px" : "0 18px",
                backgroundColor: copied ? "#10B981" : "#FFFFFF",
                color: copied ? "white" : "#0D0E20",
                border: "none",
                borderRadius: "16px",
                fontSize: isTablet ? "16px" : "14px",
                fontWeight: "600",
                fontFamily: "Inter, sans-serif",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
                flexShrink: 0,
                transition: "background-color 200ms ease, color 200ms ease",
              }}
            >
              <span>{copied ? "✓ Copied!" : "Copy"}</span>
              {!copied && (
                <img
                  src="/icons/copy-icon.png"
                  alt=""
                  style={{ width: "20px", height: "20px", objectFit: "contain" }}
                />
              )}
            </button>
          </div>

          {/* Milestone badges — always visible */}
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-around",
              alignItems: "flex-start",
              paddingTop: "4px",
            }}
          >
            {MILESTONES.map(({ number, label }) => {
              const achieved = typeof number === "number" && referralCount >= number;
              return (
                <div
                  key={number}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <div
                    style={{
                      width: `${badgeSize}px`,
                      height: `${badgeSize}px`,
                      borderRadius: "50%",
                      backgroundColor: achieved ? "#43B477" : "#FCE89C",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: badgeFontSize,
                      fontWeight: "700",
                      color: achieved ? "#FFFFFF" : "#43B477",
                      fontFamily: "Inter, sans-serif",
                      boxShadow: achieved
                        ? "0 4px 12px rgba(67,180,119,0.4)"
                        : "0 4px 10px rgba(0,0,0,0.15)",
                      transition: "background-color 300ms ease",
                    }}
                  >
                    {number}
                  </div>
                  <span
                    style={{
                      fontSize: isTablet ? "11px" : "10px",
                      color: "white",
                      fontWeight: "500",
                      fontFamily: "Inter, sans-serif",
                      textAlign: "center",
                      lineHeight: "1.3",
                      textShadow: "0 1px 4px rgba(0,0,0,0.6)",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default ShareCardInline;
